import type { DataSchema, GetViewFormItemsRes, GetViewSettingsRes, Schema } from 'types';
import type { Row } from '@tanstack/react-table';
import type { VisibilityResult } from '../../types/records.ts';
import type { ViewItem, ViewItemFile } from '../../types/catalogViewItem.ts';
import { getFormItemByIdMap } from './getFormItemByIdMap.ts';
import { getViewWidgetGrid } from './widgetGrid.ts';

interface GetBuildViewModelProps<T extends keyof P, P extends DataSchema> {
  row: Row<Schema<T, P>>;
  viewWidget?: GetViewSettingsRes;
  usableItems?: GetViewFormItemsRes;
  catalogueImagesWithUrls?: ViewItemFile[];
  dataSchema?: DataSchema;
  visibilityResults?: { [p: string]: VisibilityResult };
}

export const getBuildViewModel = <T extends keyof P, P extends DataSchema>({
  row,
  usableItems,
  viewWidget,
  catalogueImagesWithUrls,
  dataSchema,
  visibilityResults,
}: GetBuildViewModelProps<T, P>) => {
  const formItemById = getFormItemByIdMap(usableItems);

  return [...(viewWidget?.items ?? [])]
    .sort((a, b) => a.order - b.order)
    .map((widgetItem, index): ViewItem => {
      const { id, order } = widgetItem;
      const meta = formItemById.get(id);
      const visibilityResult = visibilityResults?.[id];
      const widgetGrid = getViewWidgetGrid(widgetItem, { row: index + 1, column: 1 });

      const rawValue = row.original[id as keyof Schema<T, P>] as string;

      if (!meta) {
        return {
          kind: 'unknown',
          name: 'unknown',
          title: 'unknown',
          id,
          order,
          key: id,
          value: rawValue,
          isEditable: false,
          isParametrized: false,
          isEnabled: false,
        };
      }

      switch (meta.formItemType.toLowerCase()) {
        case 'text':
        case 'number':
        case 'phonenumber':
        case 'email':
        case 'computed':
        case 'guid': {
          return {
            kind: meta.formItemType,
            id: meta.formItemId,
            order,
            ...widgetGrid,
            key: meta.formItemId,
            name: meta.formItemName,
            title: meta?.formItemTitle,
            value: rawValue,
            itemSchema: dataSchema?.[meta?.formItemId],
            visibilityResult,
            isEditable: meta?.isEditable,
            isEnabled: meta?.isEnabled,
            isParametrized: meta?.isParametrized,
          };
        }
        case 'fileupload': {
          const fileId = Array.isArray(rawValue) ? (rawValue as string[])?.[0] : '';
          const file = catalogueImagesWithUrls?.find((item) => item?.fileId === fileId)?.file;

          return {
            kind: meta.formItemType,
            id: meta.formItemId,
            order,
            ...widgetGrid,
            key: meta.formItemId,
            name: meta.formItemName,
            title: meta?.formItemTitle,
            fileId,
            file,
            itemSchema: dataSchema?.[meta?.formItemId],
            visibilityResult,
            isEditable: meta?.isEditable,
            isEnabled: meta?.isEnabled,
            isParametrized: meta?.isParametrized,
          };
        }
        case 'button': {
          return {
            kind: meta.formItemType,
            id: meta.formItemId,
            title: meta?.formItemTitle,
            name: meta.formItemName,
            order,
            ...widgetGrid,
            itemSchema: dataSchema?.[meta?.formItemId],
            visibilityResult,
            isEditable: meta?.isEditable,
            isEnabled: meta?.isEnabled,
            isParametrized: meta?.isParametrized,
          };
        }

        default:
          return {
            kind: 'unknown',
            name: 'unknown',
            title: 'unknown',
            id,
            order,
            key: id,
            value: rawValue,
            isEditable: false,
            isParametrized: false,
            isEnabled: false,
          };
      }
    });
};
